import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { createRequest, fetchCategories } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { FileText } from "lucide-react";

export default function NewRequestPage() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [cats, setCats] = useState([]);
  const [saving, setSaving] = useState(false);
  const [f, setF] = useState({
    title: "",
    description: "",
    category: "",
    budget: "",
    delivery_days: "7",
  });

  useEffect(() => { if (!loading && !user) navigate("/login"); }, [loading, user, navigate]);
  useEffect(() => { fetchCategories().then((c) => { setCats(c); setF((p) => p.category ? p : { ...p, category: c[0]?.slug || "" }); }); }, []);

  const submit = async () => {
    if (f.title.trim().length < 6) return toast.error("Title must be at least 6 characters");
    if (f.description.trim().length < 20) return toast.error("Description must be at least 20 characters");
    if (!f.category) return toast.error("Pick a category");
    const budget = Number(f.budget);
    const days = Number(f.delivery_days);
    if (!budget || budget <= 0) return toast.error("Enter a budget");
    if (!days || days <= 0) return toast.error("Enter delivery days");
    setSaving(true);
    try {
      const r = await createRequest({
        title: f.title.trim(),
        description: f.description.trim(),
        category: f.category,
        budget, delivery_days: days,
      });
      toast.success("Request posted");
      navigate(`/requests/${r.request_id}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally { setSaving(false); }
  };

  return (
    <div className="max-w-[820px] mx-auto px-6 lg:px-10 py-10">
      <div className="overline text-primary mb-2 flex items-center gap-2"><FileText className="w-4 h-4"/> ◆ NEW REQUEST</div>
      <h1 className="text-4xl font-bold tracking-tight mb-2">Tell sellers what you need</h1>
      <p className="text-muted-foreground mb-8">Be specific — better briefs get better offers.</p>

      <div className="border border-foreground/15 p-6 bg-card space-y-5">
        <div>
          <label className="overline block mb-2">◆ Title <span className="text-destructive">*</span></label>
          <Input data-testid="req-title" value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} placeholder="E.g. Design a minimalist logo for a fintech startup" className="rounded-none border-foreground/20"/>
          <p className="text-xs text-muted-foreground mt-1 mono">{f.title.length}/140</p>
        </div>
        <div>
          <label className="overline block mb-2">◆ Description <span className="text-destructive">*</span></label>
          <Textarea data-testid="req-description" value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} rows={6} placeholder="Describe scope, style, references, deliverables, and anything else sellers should know." className="rounded-none border-foreground/20"/>
          <p className="text-xs text-muted-foreground mt-1 mono">{f.description.length}/4000</p>
        </div>
        <div className="grid md:grid-cols-3 gap-4">
          <div>
            <label className="overline block mb-2">◆ Category <span className="text-destructive">*</span></label>
            <select data-testid="req-category" value={f.category} onChange={(e) => setF({ ...f, category: e.target.value })} className="w-full h-10 px-3 border border-foreground/20 rounded-none bg-background">
              {cats.map((c) => <option key={c.slug} value={c.slug}>{c.name}</option>)}
            </select>
          </div>
          <div>
            <label className="overline block mb-2">◆ Budget (USD) <span className="text-destructive">*</span></label>
            <Input data-testid="req-budget" type="number" min="1" value={f.budget} onChange={(e) => setF({ ...f, budget: e.target.value })} placeholder="200" className="rounded-none border-foreground/20 mono"/>
          </div>
          <div>
            <label className="overline block mb-2">◆ Delivery days <span className="text-destructive">*</span></label>
            <Input data-testid="req-delivery-days" type="number" min="1" value={f.delivery_days} onChange={(e) => setF({ ...f, delivery_days: e.target.value })} placeholder="7" className="rounded-none border-foreground/20 mono"/>
          </div>
        </div>
      </div>

      <div className="flex justify-between items-center mt-6">
        <button onClick={() => navigate("/requests")} className="mono text-xs text-muted-foreground hover:text-foreground">← Cancel</button>
        <button data-testid="req-submit" onClick={submit} disabled={saving} className="bg-primary text-primary-foreground px-8 py-3 mono text-xs hover:bg-primary/90 disabled:opacity-40">
          {saving ? "POSTING…" : "POST REQUEST"}
        </button>
      </div>
    </div>
  );
}
